import log from "loglevel";

export type RecentChatItem = {
    conversationSid: string;
    state?: string;
    dateCreated?: string;
    dateUpdated?: string;
    friendlyName?: string;
    serviceType?: string;
    preview?: string;
    email?: string;
};

const RECENT_CHATS_KEY = "TWILIO_RECENT_CHATS";
const MAX_RECENT = 50;

const normalizeEmail = (email?: string) => (email || "").trim().toLowerCase();

const readStore = (): Record<string, RecentChatItem[]> => {
    try {
        const raw = localStorage.getItem(RECENT_CHATS_KEY);
        if (!raw) {
            return {};
        }
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object" ? parsed : {};
    } catch (e) {
        log.warn("Failed to parse recent chats from localStorage");
        return {};
    }
};

const writeStore = (store: Record<string, RecentChatItem[]>) => {
    localStorage.setItem(RECENT_CHATS_KEY, JSON.stringify(store));
};

export const recentChatsStorage = {
    list(email?: string): RecentChatItem[] {
        const key = normalizeEmail(email);
        if (!key) {
            return [];
        }
        return readStore()[key] || [];
    },

    upsert(email: string | undefined, item: RecentChatItem) {
        const key = normalizeEmail(email);
        if (!key || !item?.conversationSid) {
            return;
        }

        const store = readStore();
        const current = store[key] || [];
        const nextItem: RecentChatItem = {
            ...item,
            email: key,
            dateUpdated: item.dateUpdated || new Date().toISOString()
        };
        const filtered = current.filter((chat) => chat.conversationSid !== item.conversationSid);
        store[key] = [nextItem, ...filtered].slice(0, MAX_RECENT);
        writeStore(store);
    },

    merge(email: string | undefined, remoteChats: RecentChatItem[]): RecentChatItem[] {
        const key = normalizeEmail(email);
        if (!key) {
            return remoteChats.slice(0, MAX_RECENT);
        }

        const local = this.list(key);
        const map = new Map<string, RecentChatItem>();

        [...remoteChats, ...local].forEach((chat) => {
            if (!chat?.conversationSid) {
                return;
            }
            const existing = map.get(chat.conversationSid);
            if (!existing) {
                map.set(chat.conversationSid, { ...chat, email: key });
                return;
            }
            const existingTime = new Date(existing.dateUpdated || existing.dateCreated || 0).getTime();
            const nextTime = new Date(chat.dateUpdated || chat.dateCreated || 0).getTime();
            if (nextTime >= existingTime) {
                map.set(chat.conversationSid, { ...existing, ...chat, email: key });
            }
        });

        const merged = Array.from(map.values()).sort((a, b) => {
            const aTime = new Date(a.dateUpdated || a.dateCreated || 0).getTime();
            const bTime = new Date(b.dateUpdated || b.dateCreated || 0).getTime();
            return bTime - aTime;
        });

        const limited = merged.slice(0, MAX_RECENT);
        const store = readStore();
        store[key] = limited;
        writeStore(store);
        return limited;
    },

    clear(email?: string) {
        const key = normalizeEmail(email);
        const store = readStore();
        if (!key) {
            localStorage.removeItem(RECENT_CHATS_KEY);
            return;
        }
        delete store[key];
        writeStore(store);
    }
};
